import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ServiceOrderCategory, ServiceSeverity, formatRobotShortId } from '@bhops/shared';
import { serviceOrdersApi } from '@/api/service-orders';
import { robotsApi } from '@/api/robots';
import { storesApi } from '@/api/stores';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';

// docs/work_log/p0-service-order-create-modal — windows audit (Phase 6)
// 에서 "serviceOrdersApi.create 호출처 0건" 으로 드러난 GAP 해결.
//
// 진입점:
//   1) RobotDetailPage 헤더의 "+ 서비스 오더" 버튼 → defaultRobotId 전달
//   2) ReportsInbox 의 "→ SO 전환" 버튼 → defaultRobotId + defaultDescription 전달
//
// 폼 구성:
//   • robotId  — 호출자가 전달 (현재 detail 페이지의 로봇으로 잠금)
//   • storeId  — robot.currentStore 에서 자동 도출 (없으면 경고)
//   • customerId — store.customerId 에서 자동 도출 (없으면 경고)
//   • category / severity / description — 사용자 입력
//   • internalNotes / scheduledFor — optional
//
// 백엔드 계약 (serviceOrdersApi.create) 는 clientId (UUID v4) 를
// (reportedById, clientId) 키로 idempotent submit 처리하므로
// 모달이 열릴 때마다 새 UUID 를 생성.

const CATEGORY_LABEL: Record<ServiceOrderCategory, string> = {
  PREVENTIVE: '정기 점검',
  REACTIVE: '고장 / 이상 대응',
  INSTALL: '신규 설치',
  REMOVAL: '회수',
  PARTS_SWAP: '부품 교체',
};

const SEVERITY_LABEL: Record<ServiceSeverity, string> = {
  P1: 'P1 긴급, 4시간 안에 해결',
  P2: 'P2 보통, 24시간 안에 해결',
  P3: 'P3 낮음, 7일 안에 해결',
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** 로봇 디테일 / 리포트 인박스에서 미리 정해진 로봇. 잠금 표시됨. */
  defaultRobotId: string;
  /** 리포트에서 SO 로 전환 시 미리 채울 본문. */
  defaultDescription?: string;
  /** 생성 직후 호출자에게 새 SO id 를 알려줌 (예: 인박스로 라우팅). */
  onCreated?: (serviceOrderId: string) => void;
}

export function ServiceOrderCreateModal({
  open,
  onClose,
  defaultRobotId,
  defaultDescription,
  onCreated,
}: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  // 모달 1회 열림당 1개의 clientId (idempotency key).
  // open 이 false → true 로 바뀔 때 새로 생성. 같은 사용자가 submit
  // 두 번 눌러도 같은 SO 가 한 번만 생성됨.
  const clientId = useMemo(() => {
    if (!open) return '';
    return globalThis.crypto?.randomUUID?.() ?? `fallback-${Date.now()}-${Math.random()}`;
  }, [open]);

  const [category, setCategory] = useState<ServiceOrderCategory>(ServiceOrderCategory.REACTIVE);
  const [severity, setSeverity] = useState<ServiceSeverity>(ServiceSeverity.P2);
  const [description, setDescription] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 모달이 다시 열릴 때 폼 초기화 + 미리채움 적용.
  useEffect(() => {
    if (!open) return;
    setCategory(ServiceOrderCategory.REACTIVE);
    setSeverity(ServiceSeverity.P2);
    setDescription(defaultDescription ?? '');
    setInternalNotes('');
    setScheduledFor('');
    setError(null);
  }, [open, defaultDescription]);

  // 로봇 → 매장 → 고객사 chain. 모달이 열렸을 때만 fetch.
  const robotQ = useQuery({
    queryKey: ['robot', defaultRobotId],
    queryFn: () => robotsApi.get(defaultRobotId),
    enabled: open && !!defaultRobotId,
  });

  const storeId = robotQ.data?.currentStore?.id ?? null;

  const storeQ = useQuery({
    queryKey: ['store', storeId],
    queryFn: () => storesApi.get(storeId!),
    enabled: open && !!storeId,
  });

  const customerId = storeQ.data?.customerId ?? null;

  const create = useMutation({
    mutationFn: () => {
      if (!customerId) {
        throw new Error('고객사를 확인할 수 없습니다. 로봇이 매장에 배치돼야 합니다.');
      }
      return serviceOrdersApi.create({
        clientId,
        robotId: defaultRobotId,
        customerId,
        storeId: storeId ?? undefined,
        category,
        severity,
        description: description.trim(),
        internalNotes: internalNotes.trim() || undefined,
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
      });
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['service-orders'] });
      qc.invalidateQueries({ queryKey: ['robot-service-orders', defaultRobotId] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`서비스 오더 ${created.humanId} 가 생성되었습니다.`, 'SO 생성 완료');
      onCreated?.(created.id);
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (!description.trim()) {
      setError('설명을 입력해 주세요.');
      return;
    }
    if (!customerId) {
      setError(
        '이 로봇은 매장에 배치되어 있지 않아 서비스 오더를 만들 수 없습니다. 먼저 매장에 배치한 뒤 다시 시도해 주세요.',
      );
      return;
    }
    create.mutate();
  }

  const robot = robotQ.data;
  const store = storeQ.data;
  const robotLabel = robot
    ? robot.serialShort
      ? formatRobotShortId(robot.serialShort)
      : (robot.serialLong ?? robot.id)
    : '불러오는 중…';
  const storeLabel = store
    ? [store.name, store.branchName].filter(Boolean).join(' ')
    : robotQ.isLoading
      ? '불러오는 중…'
      : '매장에 배치되지 않음';

  const loading = robotQ.isLoading || (!!storeId && storeQ.isLoading);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="서비스 오더 생성"
      width={520}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="so-create-form"
            disabled={create.isPending || loading || !customerId}
          >
            {create.isPending ? '생성 중…' : '생성'}
          </Button>
        </>
      }
    >
      <form id="so-create-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        {/* 로봇 / 매장 / 고객사 — 잠금 표시 (호출자 컨텍스트에서 결정) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr',
            gap: '6px 12px',
            padding: 10,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <span style={{ color: 'var(--fg-muted)' }}>로봇</span>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{robotLabel}</span>
          <span style={{ color: 'var(--fg-muted)' }}>매장</span>
          <span style={{ color: store ? undefined : 'var(--fg-danger)' }}>{storeLabel}</span>
          <span style={{ color: 'var(--fg-muted)' }}>고객사</span>
          <span style={{ color: customerId ? undefined : 'var(--fg-muted)' }}>
            {customerId ?? '매장에 연결된 고객사가 없습니다'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="분류" htmlFor="so-cat" required locked>
            <Select
              id="so-cat"
              value={category}
              onChange={(e) => setCategory(e.target.value as ServiceOrderCategory)}
            >
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="긴급도" htmlFor="so-sev" required locked>
            <Select
              id="so-sev"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as ServiceSeverity)}
            >
              {Object.entries(SEVERITY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="설명" htmlFor="so-desc" required>
          <Textarea
            id="so-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="증상이나 요청 사항을 구체적으로 적어 주세요"
            required
          />
        </Field>

        <Field label="내부 메모" htmlFor="so-notes" hint="고객에게는 보이지 않습니다.">
          <Textarea
            id="so-notes"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            rows={2}
          />
        </Field>

        <Field label="방문 예정일" htmlFor="so-sched" hint="분류한 뒤에도 바꿀 수 있습니다.">
          <Input
            id="so-sched"
            type="date"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}
